import React from "react";

import cx from "classnames";
import { keyBy } from "lodash";

import type { Choice } from "~/feedback/FeedbackItem/Question";
import { replaceLinks } from "~/feedback/utils";

import {
	CheckboxInput,
	FaceInput,
	type FaceValue,
	isCheckboxValue,
	isFaceValue,
	isRadioValue,
	isStarValue,
	isTextValue,
	isThumbValue,
	RadioInput,
	StarInput,
	type StarValue,
	TextInput,
	ThumbInput,
	type ThumbValue,
} from "../FormInputs";
import {
	type Choices,
	type ChoicesQuestion,
	type Draft,
	type DraftEntry,
	isQuestionArray,
	isValidDraft,
	type Question,
	validateDraft,
} from "./validation";

import styles from "./css/ResponseForm.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Props = ({ dataType: "validated"; questions: Question[] } | { dataType?: "raw"; questions: any }) & {
	onChange?: (changed: DraftEntry, values: Draft, errors: string[]) => void;
	readonly?: boolean;
	draft?: Draft | undefined;
	feedbackType?: "REQUESTED" | "UNSOLICITED";
};

interface State {
	responses: Record<string, string | string[] | undefined>;
	errors: Record<string, boolean | undefined>;
}

export function adaptInputToQuestionChoice(question: ChoicesQuestion, input: string | string[]) {
	switch (question.appearance) {
		case "checkbox":
			if (isCheckboxValue(input) && input.length > 0) {
				return question.choices.filter(choice => input.includes(choice.value));
			}
			return undefined;

		case "radio":
			return question.choices.find(choice => choice.value === input);

		case "stars":
			if (isStarValue(input) && input !== "none") {
				return StarInput.valueToChoiceMap[input];
			}
			return undefined;

		case "smiles":
			if (isFaceValue(input) && input !== "none") {
				return FaceInput.valueToChoiceMap[input];
			}
			return undefined;

		case "thumbs":
			if (isThumbValue(input) && input !== "none") {
				return ThumbInput.valueToChoiceMap[input];
			}
			return undefined;
	}
}

export function adaptQuestionChoiceToValue(question: ChoicesQuestion, choice: Choice | Choice[]): string | string[] {
	let valueToChoiceMap: Record<string, Choices[number]>;
	switch (question.appearance) {
		case "thumbs":
			valueToChoiceMap = ThumbInput.valueToChoiceMap;
			break;
		case "smiles":
			valueToChoiceMap = FaceInput.valueToChoiceMap;
			break;
		case "stars":
			valueToChoiceMap = StarInput.valueToChoiceMap;
			break;
		case "checkbox":
		case "radio":
			valueToChoiceMap = {};
			question.choices.forEach(choice => {
				valueToChoiceMap[choice.value] = choice;
			});
			break;
		default:
			throw new TypeError("Unsupported question appearance");
	}

	if (Array.isArray(choice)) {
		// "checkbox" appearance questions case
		const valueList = choice.map(({ value }) => `${value}`);
		const choiceOptions = Object.keys(valueToChoiceMap);

		if (valueList.some(v => !choiceOptions.includes(v))) {
			throw new Error(`Invalid choice "${JSON.stringify(choice)}"`);
		}

		return valueList;
	}

	const result = Object.entries(valueToChoiceMap).find(([, { value }]) => value === choice.value);

	if (!result) {
		throw new Error(`Invalid choice "${JSON.stringify(choice)}"`);
	}
	return result[0];
}

function draftValuesToObject(
	questionsById: Record<string, Question>,
	draft: Draft,
): Record<string, string | string[] | undefined> {
	const values: Record<string, string | string[] | undefined> = {};
	draft.forEach(({ id, value }) => {
		const question = questionsById[id];
		if (typeof value === "string") {
			values[id] = value;
		} else if (question.type === "choice") {
			values[id] = adaptQuestionChoiceToValue(question, value);
		} else {
			throw new Error("Invalid choice for question type");
		}
	});

	return values;
}

export default class ResponseForm extends React.Component<Props, State> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static calculateErrors(questions: any, draft: any): string[] {
		if (!isQuestionArray(questions) || !isValidDraft(questions, draft)) {
			validateDraft(questions, draft);
			throw new TypeError("Invalid questions or draft");
		}

		const questionsById = keyBy(questions, "id");

		const errors: string[] = [];
		draft.forEach(({ id, value }) => {
			const question = questionsById[id];
			if (question.type === "text") {
				const maxLength = TextInput.MAX_LENGTH[question.appearance];

				if (typeof value !== "string") {
					// this should be unreachable due to isValidDraft
					throw new Error("Got non-string value for text question");
				}

				if (value.length > maxLength) {
					errors.push(id);
				}
			}
		});

		return errors;
	}

	static defaultProps = {
		feedbackType: "REQUESTED",
	};

	state: State = { responses: {}, errors: {} };

	static getDerivedStateFromProps(props: Props) {
		const questionsById = keyBy(ResponseForm.getQuestions(props), "id");
		return props.draft ? { responses: draftValuesToObject(questionsById, props.draft) } : {};
	}

	static getQuestions(props: Props): Question[] {
		let questions: Question[];
		if (props.dataType === "validated") {
			questions = props.questions;
		} else if (isQuestionArray(props.questions)) {
			questions = props.questions;
		} else {
			throw new TypeError("Question data is invalid");
		}
		return questions;
	}

	get questionsById(): Record<string, Question> {
		return keyBy(this.questions, "id");
	}

	get questions(): Question[] {
		return ResponseForm.getQuestions(this.props);
	}

	valueIsValid(questionId: string, value: string): boolean {
		const question = this.questions.find(({ id }) => id === questionId);
		if (!question) {
			return false;
		}

		if (question.type === "text") {
			return value.length <= TextInput.MAX_LENGTH[question.appearance];
		}

		return true;
	}

	exportDraft(responses: State["responses"]): Draft {
		const draftEntries: Draft = [];
		Object.entries(responses).forEach(([id, value]) => {
			const question = this.questionsById[id];
			if (question.type === "choice" && value) {
				const choice = adaptInputToQuestionChoice(question, value);
				if (!choice) {
					throw new Error(`Unrecognized input "${value}"`);
				}
				draftEntries.push({ id, value: choice });
			} else if (value && typeof value === "string") {
				draftEntries.push({ id, value });
			}
		});
		return draftEntries;
	}

	onChange = (changed: State["responses"], responses: State["responses"]) => {
		const { onChange } = this.props;
		const { errors } = this.state;

		if (onChange) {
			onChange(
				this.exportDraft(changed)[0],
				this.exportDraft(responses),
				Object.keys(errors).filter(k => errors[k]),
			);
		}
	};

	changeFunctions: Record<string, ((v: string | null, error?: string) => void) | undefined> = {};

	getOnChange(id: string): (v: string | null) => void {
		const maybeFunction = this.changeFunctions[id];
		if (maybeFunction) {
			return maybeFunction;
		}

		return (this.changeFunctions[id] = (value: string | null) => {
			if (value === null) {
				const { responses } = this.state;
				delete responses[id];
				return this.onChange({}, responses);
			}

			const error = !this.valueIsValid(id, value);
			const question = this.questions.find(({ id: qId }) => id === qId);
			const isCheckboxQuestion = question?.type === "choice" && question.appearance === "checkbox";

			this.setState(({ responses, errors }) => {
				const cResponse = responses[id] ?? [];

				if (isCheckboxQuestion && isCheckboxValue(cResponse)) {
					const newResponse = cResponse.includes(value) ? cResponse.filter(r => r !== value) : [...cResponse, value];

					if (newResponse.length === 0) {
						delete responses[id];
						this.onChange({}, { ...responses });
					} else {
						this.onChange({ [id]: newResponse }, { ...responses, [id]: newResponse });
					}
				} else {
					this.onChange({ [id]: value }, { ...responses, [id]: value });
				}

				return { errors: { ...errors, [id]: error } };
			});
		});
	}

	render() {
		const { readonly, feedbackType } = this.props;
		const { responses, errors } = this.state;
		const showHeading = feedbackType === "REQUESTED";

		return (
			<form className={cx({ [styles.readonly]: readonly })} data-test-id="feedback-response-form">
				{this.questions.map(question => {
					const { id, title: originalTitle } = question;
					const isIndented = originalTitle[0] === ">";
					const title = isIndented ? originalTitle.slice(1).trim() : originalTitle;

					let titleElement = replaceLinks(title);

					// can't destructure `type` otherwise switch case won't act as type guard
					switch (question.type) {
						case "blank":
							return (
								<h3
									key={id}
									className={cx(styles.question, {
										[styles.headingQuestion]: question.appearance === "text",
										[styles.subHeadingQuestion]: question.appearance === "sub-text",
										[styles.indented]: isIndented,
									})}
								>
									{titleElement}
								</h3>
							);

						case "text": {
							const response = responses[id];
							const error = errors[id];
							let value = "";

							if (isTextValue(response)) {
								value = response;
							}

							return (
								<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
									{showHeading && (
										<div className={styles.textHeader}>
											<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{titleElement}</h4>
											{readonly || (
												<small className={cx(styles.charCount, { [styles.error]: error })}>
													{value.length}/{TextInput.MAX_LENGTH[question.appearance]}
												</small>
											)}
										</div>
									)}
									{readonly ? (
										<TextInput readonly className={styles.question} answerType={question.appearance} value={value} />
									) : (
										<TextInput
											className={styles.question}
											answerType={question.appearance}
											error={error}
											value={value}
											onChange={this.getOnChange(id)}
										/>
									)}
								</div>
							);
						}

						case "choice":
							// can't destructure `appearance` or switch case won't act as type guard
							switch (question.appearance) {
								case "checkbox": {
									const response = responses[id];
									let value: string[] | undefined = undefined;

									if (isCheckboxValue(response)) {
										value = response;
									}

									return (
										<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
											{showHeading && (
												<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{title}</h4>
											)}
											<CheckboxInput
												className={styles.question}
												readonly={readonly}
												name={id}
												value={value}
												options={question.choices.map(({ label, value }) => ({ label, value }))}
												onChange={this.getOnChange(id)}
											/>
										</div>
									);
								}

								case "radio": {
									const response = responses[id];
									let value: string | undefined = undefined;

									if (isRadioValue(response)) {
										value = response;
									}

									return (
										<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
											{showHeading && (
												<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{titleElement}</h4>
											)}
											<RadioInput
												className={styles.question}
												readonly={readonly}
												name={id}
												value={value}
												options={question.choices.map(({ label, value }) => ({ label, value }))}
												onChange={this.getOnChange(id)}
											/>
										</div>
									);
								}

								case "stars": {
									const response = responses[id];
									let value: StarValue = "none";
									if (isStarValue(response)) {
										value = response;
									}

									return (
										<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
											{showHeading && (
												<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{titleElement}</h4>
											)}
											<StarInput
												className={styles.question}
												readonly={readonly}
												name={id}
												value={value}
												onChange={this.getOnChange(id)}
											/>
										</div>
									);
								}

								case "smiles": {
									const response = responses[id];
									let value: FaceValue = "none";
									if (isFaceValue(response)) {
										value = response;
									}

									return (
										<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
											{showHeading && (
												<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{titleElement}</h4>
											)}
											<FaceInput
												className={styles.question}
												readonly={readonly}
												name={id}
												value={value}
												onChange={this.getOnChange(id)}
											/>
										</div>
									);
								}

								case "thumbs": {
									const response = responses[id];
									let value: ThumbValue = "none";
									if (isThumbValue(response)) {
										value = response;
									}

									return (
										<div key={id} className={cx(styles.question, { [styles.indented]: isIndented })}>
											{showHeading && (
												<h4 className={cx(styles.heading, { [styles.headingIndented]: isIndented })}>{titleElement}</h4>
											)}
											<ThumbInput
												className={styles.question}
												readonly={readonly}
												name={id}
												value={value}
												onChange={this.getOnChange(id)}
											/>
										</div>
									);
								}
							}
							break;

						default:
							return null;
					}
				})}
			</form>
		);
	}
}
